import { postOrderReject } from '@/api/order-list/postOrderReject';
import { useLoading } from '@/context/LoadingContext';
import { order } from '@/stores/slices/order/orderSlice';
import { fonts } from '@/theme/fonts';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

interface RejectReasonModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
}

const RejectReasonModal = ({
  open,
  onClose,
  orderId,
}: RejectReasonModalProps) => {
  const [reason, setReason] = useState<string>('');
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = useLoading();
  const { t } = useTranslation();

  const onSubmitRejectReason = useCallback(async () => {
    startLoading();
    try {
      const res = await postOrderReject({
        orderId: orderId,
        reason: reason,
      });
      if (res.isSuccess) {
        dispatch(
          order.updateOrderStatus({
            orderId: orderId,
            status: 'REJECTED',
            rejectReason: reason,
            history: res.data,
          })
        );
        setReason('');
        onClose();
      }
    } finally {
      stopLoading();
    }
  }, [dispatch, onClose, orderId, reason, startLoading, stopLoading]);

  if (!open) return null;

  return (
    <Overlay>
      <ModalBox>
        <Title>{t('wouldYouLikeToRejectThisOrder')}</Title>
        <BottomSection>
          <TextSection>
            <Textarea
              placeholder={t('pleaseEnterTheReasonForRejectingTheOrder')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <ButtonWrap>
              <Button onClick={onClose}>{t('cancel')}</Button>
              <Button
                onClick={onSubmitRejectReason}
                data-input={reason !== ''}
                disabled={reason === ''}
              >
                {t('done')}
              </Button>
            </ButtonWrap>
          </TextSection>
        </BottomSection>
      </ModalBox>
    </Overlay>
  );
};

export default RejectReasonModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  font-family: ${fonts.family.pretendard};
`;

const ModalBox = styled.div`
  width: 400px;
  height: 340px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(91, 75, 232, 0.4);
`;

const Title = styled.div`
  font-weight: ${fonts.weight.bold};
  font-size: 16px;
  line-height: 140%;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #769bf7;
  color: #ffffff;
`;

const BottomSection = styled.div`
  padding: 24px 16px;
  background: #f8f9fd;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const TextSection = styled.div`
  border: 1px solid #e2e6f4;
  border-radius: 4px;
  background: #ffffff;
  padding: 10px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Textarea = styled.textarea`
  border: none;
  outline: none;
  font-size: 15px;
  line-height: 160%;
  resize: none;
  color: #151131;
  flex: 1;
  &::placeholder {
    color: #777777;
  }
`;

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  padding: 0 46px;
`;

const Button = styled.button`
  flex: 1;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #a3a7c4;
  border-radius: 8px;
  font-weight: ${fonts.weight.semiBold};
  font-size: 14px;
  line-height: 140%;
  color: #f8f8f8;
  &[data-input='true'] {
    background: #151131;
  }
  &:disabled {
    cursor: auto;
  }
`;
